import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getClientProjectPlan, getProjectPlan } from '../../../Redux/actions';
import { THEME_COLOR } from '../../../theme/formStyles';
import { fmtDate } from '../projectSchedule.utils';
import ProjectGantt, { GanttLegend } from './ProjectGantt';
import ReportsMenu from './ReportsMenu';
import { financialYears, fyOf, projectRange } from './gantt.utils';

const toggleSx = {
    flexWrap: 'wrap',
    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500, px: 1.5, py: 0.5, color: '#6b7785' },
    '& .MuiToggleButton-root.Mui-selected': { bgcolor: '#e6f4f2', color: THEME_COLOR, '&:hover': { bgcolor: '#d7eeeb' } },
};

// One project's schedule: a "Project" tab (week columns) plus one tab per financial year (month columns,
// with the contract period). Used by the internal timeline tab and the client's Project tab.
export default function ProjectSchedulePanel({ projectId, forClient = false, reloadKey = 0, onOpenSubTask, onOpenTicket, onMessage }) {
    const dispatch = useDispatch();
    const [plan, setPlan] = useState(null);
    const [state, setState] = useState({ loading: true, error: null });
    const [tab, setTab] = useState('project');

    const load = useCallback(() => {
        setState(s => ({ ...s, loading: true, error: null }));
        dispatch((forClient ? getClientProjectPlan : getProjectPlan)(projectId)).then((res) => {
            if (res?.data?.success) { setPlan(res.data.data); setState({ loading: false, error: null }); }
            else setState({ loading: false, error: res?.data?.message || 'Could not load the schedule' });
        }).catch(() => setState({ loading: false, error: 'Could not load the schedule' }));
    }, [dispatch, projectId, forClient]);

    useEffect(() => { load(); }, [load, reloadKey]);
    useEffect(() => { setTab('project'); }, [projectId]);

    const years = useMemo(() => (plan ? financialYears(plan) : []), [plan]);
    const currentFy = `fy${fyOf(new Date())}`;

    if (state.loading && !plan) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: THEME_COLOR }} /></Box>;
    if (state.error) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{state.error}</Alert>;
    if (!plan) return null;

    const fy = years.find(y => y.key === tab);
    const range = fy ? { start: fy.start, end: fy.end } : projectRange(plan);

    return (
        <Box sx={{ opacity: state.loading ? 0.6 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
                <ToggleButtonGroup exclusive size="small" value={fy ? tab : 'project'} onChange={(e, v) => v && setTab(v)} sx={toggleSx}>
                    <ToggleButton value="project">Project</ToggleButton>
                    {years.map(y => (
                        <ToggleButton key={y.key} value={y.key}>
                            {y.label}{y.key === currentFy && <Box component="span" sx={{ ml: 0.75, width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', display: 'inline-block' }} />}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7785', display: { xs: 'none', md: 'block' } }}>
                        {plan.handover_date ? `Handover ${fmtDate(plan.handover_date, 'D MMM YYYY')}` : 'Handover date not set'}
                        {plan.contract ? ` · contract to ${fmtDate(plan.contract.end, 'MMM YYYY')}` : ''}
                    </Typography>
                    <ReportsMenu plan={plan} forClient={forClient} onMessage={onMessage} />
                </Box>
            </Box>
            <ProjectGantt key={tab} plan={plan} range={range} unit={fy ? 'month' : 'week'} showContract={!!fy}
                onOpenSubTask={onOpenSubTask} onOpenTicket={onOpenTicket} />
            <GanttLegend />
        </Box>
    );
}
